import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, Info } from "lucide-react";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import JobCardDetailModal from "./JobCardDetailModal";

function fmtPrecio(n) {
  if (n == null) return "";
  return new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 }).format(n);
}

function tituloCorto(texto, max = 110) {
  if (!texto) return "";
  const primera = texto.split(".")[0]?.trim() ?? "";
  return primera.slice(0, max);
}

const CANTIDAD_DEFECTO_POR_UNIDAD = {
  ud: 1,
  uds: 1,
  m: 5,
  ml: 5,
  "m²": 10,
  m2: 10,
  "m³": 1,
  m3: 1,
  kg: 10,
  l: 5,
  h: 1,
};

function cantidadDefecto(unidad) {
  const u = (unidad ?? "").trim().toLowerCase();
  return CANTIDAD_DEFECTO_POR_UNIDAD[u] ?? 1;
}

export default function EditLineModal({ open, onClose, intencion, onSave, loading }) {
  const candidatas = intencion?.candidatas ?? [];
  const unidad = intencion?.unidad ?? candidatas[0]?.unidad ?? "ud";

  const [seleccion, setSeleccion] = useState(intencion?.jobcard_id ?? candidatas[0]?.id ?? null);
  const [cantidad, setCantidad] = useState(intencion?.cantidad ?? cantidadDefecto(unidad));
  const [detalle, setDetalle] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSeleccion(intencion?.jobcard_id ?? candidatas[0]?.id ?? null);
    setCantidad(intencion?.cantidad ?? cantidadDefecto(unidad));
  }, [open, intencion]); // eslint-disable-line react-hooks/exhaustive-deps

  const opciones = candidatas;

  const cantidadValida = (() => {
    const n = Number(cantidad);
    return Number.isFinite(n) && n > 0;
  })();
  const ready = !!seleccion && cantidadValida;

  const handleSave = () => {
    if (!ready) return;
    onSave({
      area: intencion.area,
      jobcard_id: seleccion,
      cantidad: Number(cantidad),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="edit-line-modal">
        <DialogHeader>
          <DialogTitle>Modificar partida</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-2 max-h-[420px] overflow-y-auto pr-1">
            {opciones.map((c) => {
              const selected = seleccion === c.id;
              return (
                <div
                  key={c.id}
                  className={`relative rounded-md border transition-all ${
                    selected
                      ? "border-blue-600 bg-blue-50/60 ring-2 ring-blue-600"
                      : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setSeleccion(c.id)}
                    className="text-left w-full px-4 py-3 pr-12"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-sm text-slate-800 leading-snug flex-1">
                        {tituloCorto(c.texto)}
                      </div>
                      {c.coste_total != null && (
                        <div className="text-sm font-semibold text-blue-700 tabular-nums whitespace-nowrap">
                          {fmtPrecio(c.coste_total)} €/{c.unidad ?? unidad}
                        </div>
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDetalle(c);
                    }}
                    title="Ver detalle"
                    className="absolute top-1/2 -translate-y-1/2 right-2 p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-white/80"
                  >
                    <Info className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="max-w-xs">
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 block">
              Cantidad ({unidad})
            </Label>
            <Input
              type="number"
              min="0"
              step="any"
              value={cantidad ?? ""}
              onChange={(e) => setCantidad(e.target.value)}
              data-testid="edit-line-cantidad"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <Button variant="outline" onClick={onClose} className="border-slate-300">
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={!ready || loading}
              className="bg-blue-600 hover:bg-blue-700 gap-2"
              data-testid="edit-line-save-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Guardando…
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" /> Guardar cambios
                </>
              )}
            </Button>
          </div>
        </div>

        <JobCardDetailModal
          open={!!detalle}
          onClose={() => setDetalle(null)}
          jobcard={detalle}
        />
      </DialogContent>
    </Dialog>
  );
}
